import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, getClassPointRankingMode, getTotalEarnedPointsByStudent, waitForAuthReady } from '../../../utils/firebase';
import {
  computeSalaryDeduction,
  parseSalaryTaxSettings,
  DEFAULT_SALARY_TAX_SETTINGS,
  type SalaryTaxSettings,
} from '../../../utils/salaryTax';
import { format } from 'date-fns';
import ko from 'date-fns/locale/ko';
import { useIsMobile } from '../../../utils/useMediaQuery';
import Button from '../../../components/ui/Button';
import DownIcon from '../../../components/icons/DownIcon';
import CalendarIcon from '../../../components/icons/CalendarIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import SalaryPayslipBreakdown from '../../../components/ui/SalaryPayslipBreakdown';

interface PointHistory {
  id: string;
  studentId?: string;
  uid?: string;
  amount: number;
  reason: string;
  date: Date;
  type: 'oneTime' | 'daily' | 'weekly' | 'monthly' | string;
  teacherId?: string;
  schoolId?: string;
  createdAt: Date;
  grossAmount?: number;
  taxLines?: { label: string; amount: number }[];
  totalTax?: number;
  kind?: 'salary';
}

interface StudentRank {
  id: string;
  name: string;
  points: number;
  rank: number;
}

interface StudentRole {
  roleId: string;
  roleName: string;
  isActive: boolean;
  /** classRoles.salary (포인트 급여 총액) */
  salary?: number;
  monthlySalary?: number;
  salaryType?: 'point' | 'stamp';
  salaryPeriod?: 'daily' | 'weekly' | 'monthly';
}

/** 급여 명세(세전·공제·실수령)를 보여줄 수 있는 내역인지 */
const isSalaryPayslipEntry = (h: PointHistory) =>
  h.kind === 'salary' ||
  h.grossAmount != null ||
  (Array.isArray(h.taxLines) && h.taxLines.length > 0);

const ITEMS_PER_PAGE = 5;
const RANK_ITEMS_PER_PAGE = 5;

const StudentClassPoints: React.FC = () => {
  const isMobile = useIsMobile();
  const { currentUser } = useAuth();
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [myRank, setMyRank] = useState<number>(0);
  const [topStudents, setTopStudents] = useState<StudentRank[]>([]);
  const [pointHistory, setPointHistory] = useState<PointHistory[]>([]);
  const [myRoles, setMyRoles] = useState<StudentRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0); // 0: 전체, 1: 지급, 2: 사용, 3: 급여 명세
  const [currentPage, setCurrentPage] = useState(1);
  const [rankCurrentPage, setRankCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'recent' | 'oldest'>('recent');
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });
  const [payslipOpenId, setPayslipOpenId] = useState<string | null>(null);
  const [salaryTaxSettings, setSalaryTaxSettings] = useState<SalaryTaxSettings>(DEFAULT_SALARY_TAX_SETTINGS);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !currentUser.classId) {
        setError('학급 정보를 찾을 수 없습니다.');
        return;
      }

      try {
        setLoading(true);
        await waitForAuthReady();
        console.log('Current user:', currentUser);
        console.log('ClassId:', currentUser.classId);
        
        // 1. 포인트 등수 기준 설정 확인
        const rankingMode = await getClassPointRankingMode(currentUser.schoolId, currentUser.classId);

        // 2. 학생 목록 및 등수용 포인트 가져오기
        const studentsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students`);
        const q = query(studentsRef);
        const querySnapshot = await getDocs(q);

        let pointsByStudent: Record<string, number> = {};
        if (rankingMode === 'totalEarned') {
          pointsByStudent = await getTotalEarnedPointsByStudent(currentUser.schoolId, currentUser.classId);
        }

        // 포인트 기준으로 정렬된 학생 데이터 생성 (등수용 포인트 = balance 또는 totalEarned)
        const studentsData = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            const balance = data.points || 0;
            const rankPoints = rankingMode === 'totalEarned' ? (pointsByStudent[doc.id] || 0) : balance;
            return {
              id: doc.id,
              name: data.name,
              points: rankPoints
            };
          })
          .sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points;
            return a.name.localeCompare(b.name);
          })
          .map((student, index) => ({
            ...student,
            rank: index + 1
          }));

        setTopStudents(studentsData);

        const myRankData = studentsData.find(student => student.id === currentUser.uid);
        setMyRank(myRankData?.rank || 0);
        // 표시용 포인트: 등수에는 rankPoints 쓰지만, "내 포인트"는 현재 보유 포인트로 표시
        const myBalance = querySnapshot.docs.find(d => d.id === currentUser.uid)?.data()?.points ?? 0;
        setTotalPoints(myBalance);

        // 2. 포인트 내역 가져오기
        const studentId = currentUser.uid;
        if (!studentId) {
          throw new Error('학생 정보를 찾을 수 없습니다.');
        }
        
        // 포인트 내역 컬렉션 참조 (studentId / uid 둘 다 조회 후 병합 — 레거시 호환)
        const historyRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/pointHistories`);
        const historyQueryByStudentId = query(
          historyRef,
          where('studentId', '==', studentId),
          orderBy('date', 'desc')
        );
        const historyQueryByUid = query(historyRef, where('uid', '==', studentId));

        const [historySnapshot1, historySnapshot2] = await Promise.all([
          getDocs(historyQueryByStudentId),
          getDocs(historyQueryByUid)
        ]);

        const byId = new Map<string, PointHistory>();
        const mapDoc = (d: (typeof historySnapshot1.docs)[0]) => {
          const data = d.data();
          const row = {
            id: d.id,
            ...data,
            date: data.date?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date()
          } as PointHistory;
          byId.set(d.id, row);
        };
        historySnapshot1.docs.forEach(mapDoc);
        historySnapshot2.docs.forEach(mapDoc);

        const historyData = Array.from(byId.values()).sort(
          (a, b) => b.date.getTime() - a.date.getTime()
        );

        setPointHistory(historyData);

        try {
          const taxRef = doc(
            db,
            `schools/${currentUser.schoolId}/classes/${currentUser.classId}/salaryTaxSettings`,
            'default'
          );
          const taxSnap = await getDoc(taxRef);
          setSalaryTaxSettings(
            parseSalaryTaxSettings(taxSnap.data() as Record<string, unknown> | undefined)
          );
        } catch (taxErr) {
          console.error('급여 세금 설정 조회 오류:', taxErr);
          setSalaryTaxSettings({ ...DEFAULT_SALARY_TAX_SETTINGS });
        }

        // 3. 학생의 역할 정보 가져오기
        const student = currentUser as any;
        console.log('전체 학생 데이터:', student);
        console.log('assignedRoles 타입:', typeof student.assignedRoles);
        console.log('assignedRoles 배열 여부:', Array.isArray(student.assignedRoles));
        
        if (student.assignedRoles && Array.isArray(student.assignedRoles)) {
          console.log('학생의 할당된 역할들:', student.assignedRoles);
          console.log('역할 개수:', student.assignedRoles.length);
          
          const rolesData: StudentRole[] = [];
          
          // 모든 classRoles 문서를 조회하여 학생의 역할 찾기
          try {
            const classRolesRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/classRoles`);
            const classRolesQuery = query(classRolesRef);
            const classRolesSnapshot = await getDocs(classRolesQuery);
            
            console.log('전체 classRoles 문서 수:', classRolesSnapshot.size);
            
            // 각 classRoles 문서 확인
            for (const classRoleDoc of classRolesSnapshot.docs) {
              const roleData = classRoleDoc.data();
              console.log(`문서 ID: ${classRoleDoc.id}, 내부 ID: ${roleData.id}`);
              
              // 학생이 이 역할을 가지고 있는지 확인
              const hasRole = student.assignedRoles.some((assignedRole: any) => 
                assignedRole.roleId === roleData.id || assignedRole.roleId === classRoleDoc.id
              );
              
              if (hasRole) {
                console.log(`학생이 역할 ${roleData.name} (ID: ${roleData.id})을 가지고 있습니다.`);
                const gross =
                  typeof roleData.salary === 'number'
                    ? roleData.salary
                    : typeof roleData.monthlySalary === 'number'
                      ? roleData.monthlySalary
                      : 0;
                rolesData.push({
                  roleId: roleData.id || classRoleDoc.id,
                  roleName: roleData.name || '알 수 없는 역할',
                  isActive: true,
                  salary: gross,
                  monthlySalary: roleData.monthlySalary,
                  salaryType: roleData.salaryType ?? 'point',
                  salaryPeriod: roleData.salaryPeriod,
                });
              }
            }
          } catch (roleErr) {
            console.error('classRoles 조회 중 오류:', roleErr);
          }
          
          console.log('가져온 역할 데이터:', rolesData);
          setMyRoles(rolesData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // 필터링된 포인트 내역
  const filteredHistory = useMemo(() => {
    if (selectedTab === 3) {
      return [];
    }
    let filtered = [...pointHistory];

    // 탭 필터
    if (selectedTab === 1) {
      filtered = filtered.filter(h => h.amount > 0);
    } else if (selectedTab === 2) {
      filtered = filtered.filter(h => h.amount < 0);
    }

    // 날짜 필터
    if (dateRange.start) {
      filtered = filtered.filter(h => {
        const historyDate = new Date(h.date);
        historyDate.setHours(0, 0, 0, 0);
        return historyDate >= dateRange.start!;
      });
    }
    if (dateRange.end) {
      filtered = filtered.filter(h => {
        const historyDate = new Date(h.date);
        historyDate.setHours(23, 59, 59, 999);
        return historyDate <= dateRange.end!;
      });
    }

    // 정렬
    if (sortOrder === 'recent') {
      filtered.sort((a, b) => b.date.getTime() - a.date.getTime());
    } else {
      filtered.sort((a, b) => a.date.getTime() - b.date.getTime());
    }

    return filtered;
  }, [pointHistory, selectedTab, dateRange, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab]);

  // 페이지네이션된 포인트 내역
  const paginatedHistory = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredHistory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredHistory, currentPage]);

  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);

  // 순위 페이지네이션
  const paginatedRank = useMemo(() => {
    const startIndex = (rankCurrentPage - 1) * RANK_ITEMS_PER_PAGE;
    return topStudents.slice(startIndex, startIndex + RANK_ITEMS_PER_PAGE);
  }, [topStudents, rankCurrentPage]);

  const rankTotalPages = Math.ceil(topStudents.length / RANK_ITEMS_PER_PAGE);

  // 순위 배지 색상
  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) return '#FF6F0F';
    if (rank === 2) return '#FF9F5E';
    if (rank === 3) return '#FFD2B5';
    return 'transparent';
  };

  // 순위 배지 텍스트 색상
  const getRankBadgeTextColor = (rank: number) => {
    if (rank <= 3) return 'white';
    return '#161616';
  };

  // 타입 한글 변환
  const getTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'oneTime': '일회성',
      'daily': '일일',
      'weekly': '주간',
      'monthly': '월급',
      'auction': '경매 입찰',
      'purchase': '구매',
    };
    return typeMap[type] || type;
  };

  // 급여 명세 가능한 포인트 내역(최신순) — 급여 명세 탭·지급 내역용
  const salaryPayslipHistory = useMemo(() => {
    return pointHistory
      .filter(isSalaryPayslipEntry)
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [pointHistory]);

  /** 포인트 역할 급여 합(표시용) — stamp 역할은 제외 */
  const totalMonthlySalary = useMemo(() => {
    return myRoles.reduce((sum, role) => {
      if (role.salaryType === 'stamp') return sum;
      return sum + (role.salary ?? role.monthlySalary ?? 0);
    }, 0);
  }, [myRoles]);

  /** 역할별 지급 예상 명세(교사 화면과 동일한 공제 로직) */
  const roleSalaryPreviews = useMemo(() => {
    return myRoles
      .filter((r) => r.salaryType !== 'stamp')
      .map((role) => {
        const gross = role.salary ?? role.monthlySalary ?? 0;
        return {
          role,
          deduction: computeSalaryDeduction(gross, salaryTaxSettings),
        };
      })
      .filter((row) => row.deduction.gross > 0);
  }, [myRoles, salaryTaxSettings]);

  // 포인트 내역을 월별로 그룹화 (모바일용)
  const groupedHistory = useMemo(() => {
    const grouped = filteredHistory.reduce((acc, item) => {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push(item);
      return acc;
    }, {} as Record<string, typeof filteredHistory>);
    
    return grouped;
  }, [filteredHistory]);

  // 포인트 내역에 balance 계산 추가 (모바일용)
  const historyWithBalance = useMemo(() => {
    return filteredHistory.map((item, index) => {
      // 현재 포인트부터 역순으로 계산
      let balance = totalPoints;
      
      // 이 항목 이후의 모든 내역을 역순으로 계산
      for (let i = index + 1; i < filteredHistory.length; i++) {
        const laterItem = filteredHistory[i];
        if (laterItem.amount > 0) {
          balance -= laterItem.amount;
        } else {
          balance += Math.abs(laterItem.amount);
        }
      }
      
      return {
        ...item,
        balance
      };
    });
  }, [filteredHistory, totalPoints]);

  // 포맷팅 함수
  const formatPoints = (points: number) => {
    return points.toLocaleString('ko-KR');
  };

  const formatDate = (date: Date) => {
    const d = new Date(date);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #FF6F0F', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{
          padding: '16px 20px',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c33'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative', 
      background: '#F4F6FA', 
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      padding: '0'
    }}>
      {/* 헤더 */}
      <div style={{ 
        width: '100%', 
        marginBottom: isMobile ? '16px' : '24px',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '16px',
        flexShrink: 0,
        padding: isMobile ? '0 20px' : '0'
      }}>
        <div style={{
          color: '#161616',
          fontSize: isMobile ? '24px' : '32px',
          fontFamily: 'Pretendard, sans-serif',
          fontWeight: '600',
          lineHeight: isMobile ? '36px' : '48px'
        }}>
          포인트
        </div>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '16px',
        width: '100%',
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0,
        padding: isMobile ? '0 20px 20px 20px' : '0'
      }}>
        {/* 왼쪽 사이드바: 포인트 현황 + 순위 */}
        <div style={{
          width: isMobile ? '100%' : '372px',
          height: isMobile ? 'auto' : '726px',
          padding: isMobile ? '16px' : '16px 20px',
          background: 'white',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '24px' : '48px',
          overflow: isMobile ? 'visible' : 'hidden'
        }}>
          {/* 내 포인트 현황 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', flexShrink: 0 }}>
            <div style={{
              color: '#161616',
              fontSize: '18px',
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: '27px'
            }}>
              내 포인트 현황
            </div>

            <div style={{
              width: '100%',
              padding: '16px',
              background: '#F8F8F8',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* 역할 표시 */}
                {myRoles.length > 0 && (
                  <div style={{
                    padding: '8px 20px',
                    background: 'white',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      color: '#161616',
                      fontSize: '13px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      내 역할
                    </div>
                    <div style={{
                      color: '#FF6F0F',
                      fontSize: '13px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      {myRoles.map(r => r.roleName).join(', ')}
                    </div>
                  </div>
                )}

                {/* 포인트 정보 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
                    <div style={{
                      color: '#161616',
                      fontSize: '13px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      {currentUser?.name}님의 보유 포인트
                    </div>
                    <div style={{
                      color: '#FF6F0F',
                      fontSize: '28px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '600',
                      lineHeight: '42px',
                      textAlign: 'center'
                    }}>
                      {totalPoints.toLocaleString()} P
                    </div>
                  </div>

                  {/* 순위 및 급여 */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div style={{
                      flex: '1 1 0',
                      borderRight: '1px solid #E9E9E9',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      paddingRight: '8px'
                    }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: '19.5px',
                        textAlign: 'center'
                      }}>
                        현재 나의 순위
                      </div>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: '24px',
                        textAlign: 'center'
                      }}>
                        {myRank}위
                      </div>
                    </div>
                    <div style={{
                      flex: '1 1 0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: '8px'
                    }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: '19.5px',
                        textAlign: 'center'
                      }}>
                        💰 급여
                      </div>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: '24px',
                        textAlign: 'center'
                      }}>
                        {totalMonthlySalary} P
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 학급 순위 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', flex: 1, minHeight: 0 }}>
            <div style={{
              color: '#161616',
              fontSize: '18px',
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: '27px',
              flexShrink: 0
            }}>
              학급 순위
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', flex: 1, minHeight: 0 }}>
              {/* 테이블 헤더 */}
              <div style={{
                padding: '4px 8px',
                background: '#F8F8F8',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexShrink: 0
              }}>
                <div style={{ width: '80px', padding: '0 2px' }}>
                  <div style={{
                    color: '#4C4C4C',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    순위
                  </div>
                </div>
                <div style={{ width: '124px' }}>
                  <div style={{
                    color: '#4C4C4C',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    이름
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: '#4C4C4C',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    포인트
                  </div>
                </div>
              </div>

              {/* 순위 리스트와 페이지네이션 컨테이너 */}
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* 순위 리스트 */}
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {paginatedRank.map((student) => (
                    <div
                      key={student.id}
                      style={{
                        padding: '8px',
                        borderBottom: '1px solid #F0F0F0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <div style={{ width: '80px', display: 'flex', justifyContent: 'flex-start' }}>
                        <div style={{
                          padding: '2px 12px',
                          background: getRankBadgeColor(student.rank),
                          borderRadius: '8px',
                          display: 'inline-flex'
                        }}>
                          <div style={{
                            color: getRankBadgeTextColor(student.rank),
                            fontSize: '12px',
                            fontFamily: 'Pretendard, sans-serif',
                            fontWeight: '500',
                            lineHeight: '18px'
                          }}>
                            {student.rank}위
                          </div>
                        </div>
                      </div>
                      <div style={{ width: '124px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '1px solid #F0F0F0',
                          borderRadius: '4px'
                        }} />
                        <div style={{
                          color: '#161616',
                          fontSize: '13px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          {student.name}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: '#161616',
                          fontSize: '13px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          {student.points.toLocaleString()} P
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 순위 페이지네이션 */}
                {rankTotalPages > 1 && (
                  <div style={{
                    padding: '20px 0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div
                      onClick={() => setRankCurrentPage(1)}
                      style={{
                        cursor: rankCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: rankCurrentPage === 1 ? 0.5 : 1
                      }}
                    >
                      <LeftArrowDoubleIcon width={24} height={24} color="#989898" />
                    </div>
                    <div
                      onClick={() => setRankCurrentPage(prev => Math.max(1, prev - 1))}
                      style={{
                        cursor: rankCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: rankCurrentPage === 1 ? 0.5 : 1
                      }}
                    >
                      <LeftArrowIcon width={24} height={24} color="#989898" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {Array.from({ length: Math.min(3, rankTotalPages) }, (_, i) => {
                      let pageNum: number;
                      if (rankTotalPages <= 3) {
                        pageNum = i + 1;
                      } else if (rankCurrentPage <= 2) {
                        pageNum = i + 1;
                      } else if (rankCurrentPage >= rankTotalPages - 1) {
                        pageNum = rankTotalPages - 2 + i;
                      } else {
                        pageNum = rankCurrentPage - 1 + i;
                      }
                      return (
                        <div
                          key={pageNum}
                          onClick={() => setRankCurrentPage(pageNum)}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{
                            textAlign: 'center',
                            color: rankCurrentPage === pageNum ? '#161616' : '#989898',
                            fontSize: '14px',
                            fontFamily: 'Pretendard, sans-serif',
                            fontWeight: '500',
                            lineHeight: '14px'
                          }}>
                            {pageNum}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div
                      onClick={() => setRankCurrentPage(prev => Math.min(rankTotalPages, prev + 1))}
                      style={{
                        cursor: rankCurrentPage === rankTotalPages ? 'not-allowed' : 'pointer',
                        opacity: rankCurrentPage === rankTotalPages ? 0.5 : 1
                      }}
                    >
                      <RightArrowIcon width={24} height={24} color={rankCurrentPage === rankTotalPages ? '#989898' : '#161616'} />
                    </div>
                    <div
                      onClick={() => setRankCurrentPage(rankTotalPages)}
                      style={{
                        cursor: rankCurrentPage === rankTotalPages ? 'not-allowed' : 'pointer',
                        opacity: rankCurrentPage === rankTotalPages ? 0.5 : 1
                      }}
                    >
                      <RightArrowDoubleIcon width={24} height={24} color={rankCurrentPage === rankTotalPages ? '#989898' : '#161616'} />
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 메인 영역: 포인트 내역 */}
        <div style={{
          flex: 1,
          height: isMobile ? 'auto' : '726px',
          padding: isMobile ? '16px' : '16px 20px',
          background: 'white',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '16px' : '24px',
          overflow: isMobile ? 'visible' : 'hidden'
        }}>
          <div style={{
            color: '#161616',
            fontSize: '18px',
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: '27px',
            flexShrink: 0
          }}>
            내 포인트 내역
          </div>

          {/* 필터 및 정렬 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: isMobile ? '12px' : '20px',
            flexWrap: 'wrap',
            flexShrink: 0,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            {/* 탭 필터 */}
            <div style={{ display: 'flex', gap: '16px', flex: '1 1 0', flexWrap: 'wrap', alignItems: 'center' }}>
              <div
                onClick={() => setSelectedTab(0)}
                style={{
                  minHeight: '40px',
                  maxHeight: '40px',
                  padding: '8px 12px',
                  background: selectedTab === 0 ? '#FF6F0F' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  color: selectedTab === 0 ? 'white' : '#161616',
                  fontSize: '14px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '500',
                  lineHeight: '21px'
                }}>
                  전체
                </div>
              </div>
              <div
                onClick={() => setSelectedTab(1)}
                style={{
                  minHeight: '40px',
                  maxHeight: '40px',
                  padding: '8px 12px',
                  background: selectedTab === 1 ? '#FF6F0F' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  color: selectedTab === 1 ? 'white' : '#161616',
                  fontSize: '14px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '500',
                  lineHeight: '21px'
                }}>
                  지급
                </div>
              </div>
              <div
                onClick={() => setSelectedTab(2)}
                style={{
                  minHeight: '40px',
                  maxHeight: '40px',
                  padding: '8px 12px',
                  background: selectedTab === 2 ? '#FF6F0F' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  color: selectedTab === 2 ? 'white' : '#161616',
                  fontSize: '14px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '500',
                  lineHeight: '21px'
                }}>
                  사용
                </div>
              </div>
              <div
                onClick={() => setSelectedTab(3)}
                style={{
                  minHeight: '40px',
                  maxHeight: '40px',
                  padding: '8px 12px',
                  background: selectedTab === 3 ? '#FF6F0F' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    color: selectedTab === 3 ? 'white' : '#161616',
                    fontSize: '14px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '21px',
                  }}
                >
                  급여 명세
                </div>
              </div>
            </div>

            {/* 날짜 필터 및 정렬 */}
            {selectedTab !== 3 && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {/* 날짜 필터 */}
              <div style={{
                height: '40px',
                padding: '8px 12px',
                background: '#293044',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                position: 'relative'
              }}>
                <CalendarIcon width={20} height={20} fill="white" />
                <div style={{
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '500',
                  lineHeight: '21px'
                }}>
                  {dateRange.start && dateRange.end
                    ? `${format(dateRange.start, 'yyyy.MM.dd')} ~ ${format(dateRange.end, 'yyyy.MM.dd')}`
                    : dateRange.start
                    ? `${format(dateRange.start, 'yyyy.MM.dd')} ~`
                    : '날짜 선택'}
                </div>
                <input
                  type="date"
                  onChange={(e) => {
                    if (e.target.value) {
                      const date = new Date(e.target.value);
                      if (!dateRange.start || (dateRange.end && date >= dateRange.start)) {
                        setDateRange(prev => ({ ...prev, end: date }));
                      } else {
                        setDateRange(prev => ({ ...prev, start: date, end: null }));
                      }
                    }
                  }}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer'
                  }}
                />
              </div>

              {/* 정렬 드롭다운 */}
              <div style={{ position: 'relative' }}>
                <Button
                  width="114px"
                  height="42px"
                  size="medium"
                  padding="12px"
                  variant="line"
                  rightIcon={<DownIcon width={24} height={24} fill="#161616" />}
                  onClick={() => {
                    // 드롭다운 토글 로직 (필요시 추가)
                  }}
                >
                  {sortOrder === 'recent' ? '최근 등록 순' : '오래된 순'}
                </Button>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'recent' | 'oldest')}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 1
                  }}
                >
                  <option value="recent">최근 등록 순</option>
                  <option value="oldest">오래된 순</option>
                </select>
              </div>
            </div>
            )}
          </div>

          {/* 포인트 내역 테이블 */}
          {selectedTab === 3 ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 'clamp(24px, 4vw, 36px)',
                paddingTop: '4px',
              }}
            >
              {roleSalaryPreviews.length > 0 && (
                <section style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div
                    style={{
                      color: 'var(--Color-text-strong)',
                      fontSize: '20px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: 600,
                      lineHeight: '30px',
                    }}
                  >
                    받을 급여 예상 명세
                  </div>
                  <div
                    style={{
                      color: '#989898',
                      fontSize: '14px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: 400,
                      lineHeight: '21px',
                    }}
                  >
                    내 역할 급여(총액)에 담임이 설정한 공제가 반영된 예상 지급액입니다. 실제 지급 시에도 같은 방식으로 적용됩니다.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {roleSalaryPreviews.map(({ role, deduction }) => (
                      <div key={role.roleId}>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: 600,
                            color: '#4C4C4C',
                            marginBottom: '10px',
                            fontFamily: 'Pretendard, sans-serif',
                            lineHeight: '22px',
                          }}
                        >
                          {role.roleName}
                          {role.salaryPeriod ? ` · ${getTypeLabel(role.salaryPeriod)}` : ''}
                        </div>
                        <SalaryPayslipBreakdown
                          variant="default"
                          showTitle={false}
                          grossAmount={deduction.gross}
                          taxLines={deduction.taxLines}
                          totalTax={deduction.totalTax}
                          netAmount={deduction.net}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {salaryPayslipHistory.length > 0 && (
                <section style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div
                    style={{
                      color: 'var(--Color-text-strong)',
                      fontSize: '20px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: 600,
                      lineHeight: '30px',
                    }}
                  >
                    지급된 급여 명세
                  </div>
                  <div
                    style={{
                      color: '#989898',
                      fontSize: '14px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: 400,
                      lineHeight: '21px',
                    }}
                  >
                    역할 급여가 지급된 건의 세전·공제·실수령입니다. 다른 탭의 포인트 내역에서도 같은 내용을 펼쳐 볼 수 있습니다.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {salaryPayslipHistory.map((h) => (
                      <div key={h.id}>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: 500,
                            color: '#4C4C4C',
                            marginBottom: '10px',
                            fontFamily: 'Pretendard, sans-serif',
                            lineHeight: '22px',
                          }}
                        >
                          {format(h.date, 'yyyy.MM.dd HH:mm', { locale: ko })} · {getTypeLabel(h.type)} · {h.reason}
                        </div>
                        <SalaryPayslipBreakdown
                          variant="default"
                          showTitle={false}
                          grossAmount={h.grossAmount}
                          taxLines={h.taxLines}
                          totalTax={h.totalTax}
                          netAmount={h.amount}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {roleSalaryPreviews.length === 0 && salaryPayslipHistory.length === 0 && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                    padding: '48px 24px',
                    textAlign: 'center',
                    color: '#989898',
                    fontSize: '15px',
                    fontFamily: 'Pretendard, sans-serif',
                    lineHeight: '22px',
                  }}
                >
                  급여 역할이 없거나 지급된 급여 내역이 없어 표시할 명세가 없습니다.
                </div>
              )}
            </div>
          ) : isMobile ? (
            /* 모바일: StudentPointEditDialog 스타일 */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '16px' : '24px',
              overflow: 'visible',
              paddingTop: '0.375rem'
            }}>
              {/* 내역 리스트 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '28px'
              }}>
                {Object.keys(groupedHistory).length > 0 ? (
                  Object.entries(groupedHistory).map(([month, items]) => (
                    <div key={month} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      width: '100%'
                    }}>
                      {/* 월 헤더 */}
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        lineHeight: '150%',
                        color: 'var(--Color-text-assistive, #989898)',
                        wordWrap: 'break-word'
                      }}>
                        {month}
                      </span>

                      {/* 해당 월의 내역들 */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.75rem'
                      }}>
                        {items.map((item) => {
                          const itemWithBalance = historyWithBalance.find(h => h.id === item.id);
                          return (
                            <div
                              key={item.id}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                {/* 아이콘 (임시 박스) */}
                                <div style={{
                                  width: isMobile ? '2.5rem' : '2.75rem',
                                  height: isMobile ? '2.5rem' : '2.75rem',
                                  border: '0.77px solid var(--Color-line-normal, #F0F0F0)',
                                  borderRadius: '0.25rem',
                                  flexShrink: 0
                                }} />

                                {/* 내역 정보 */}
                                <div style={{
                                  width: isMobile ? 'calc(100% - 3.5rem)' : '21.4375rem',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start'
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.25rem'
                                  }}>
                                    <span style={{
                                      fontSize: '0.8125rem',
                                      fontWeight: '500',
                                      lineHeight: '150%',
                                      color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                                      wordWrap: 'break-word'
                                    }}>
                                      {item.amount > 0 ? '적립' : '차감'} ({item.reason})
                                    </span>
                                    <span style={{
                                      fontSize: '0.8125rem',
                                      fontWeight: '500',
                                      lineHeight: '150%',
                                      color: 'var(--Color-text-assistive, #989898)',
                                      wordWrap: 'break-word'
                                    }}>
                                      {formatDate(item.date)}
                                    </span>
                                    {isSalaryPayslipEntry(item) && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setPayslipOpenId((p) => (p === item.id ? null : item.id))
                                        }
                                        style={{
                                          alignSelf: 'flex-start',
                                          padding: 0,
                                          border: 'none',
                                          background: 'none',
                                          cursor: 'pointer',
                                          fontSize: '0.75rem',
                                          fontWeight: '500',
                                          color: 'var(--Color-fill-brand-color, #FF6F0F)',
                                          textDecoration: 'underline',
                                        }}
                                      >
                                        {payslipOpenId === item.id ? '급여 명세 접기' : '급여 명세 보기'}
                                      </button>
                                    )}
                                  </div>

                                  <div style={{
                                    width: '2.5rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    gap: '0.25rem'
                                  }}>
                                    <span style={{
                                      textAlign: 'right',
                                      fontSize: '0.8125rem',
                                      fontWeight: '600',
                                      lineHeight: '150%',
                                      color: item.amount > 0 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
                                      wordWrap: 'break-word'
                                    }}>
                                      {item.amount > 0 ? '+' : ''}{item.amount}P
                                    </span>
                                    <span style={{
                                      textAlign: 'right',
                                      fontSize: '0.75rem',
                                      fontWeight: '500',
                                      lineHeight: '150%',
                                      color: 'var(--Color-text-assistive, #989898)',
                                      wordWrap: 'break-word'
                                    }}>
                                      {formatPoints(itemWithBalance?.balance || 0)}P
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {isSalaryPayslipEntry(item) && payslipOpenId === item.id && (
                                <div style={{ marginLeft: isMobile ? '0' : '3rem' }}>
                                  <SalaryPayslipBreakdown
                                    variant="compact"
                                    grossAmount={item.grossAmount}
                                    taxLines={item.taxLines}
                                    totalTax={item.totalTax}
                                    netAmount={item.amount}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{
                    padding: '1.25rem',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '0.875rem'
                  }}>
                    포인트 내역이 없습니다.
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* 데스크톱: 테이블 형태 */
            <div style={{
              flex: 1,
              minHeight: 0,
              opacity: 0.95,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* 테이블 헤더 */}
              <div style={{
                borderBottom: '1px solid #F0F0F0',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{ width: '103px', padding: '12px 16px' }}>
                  <div style={{
                    color: '#989898',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    No
                  </div>
                </div>
                <div style={{ width: '166px', padding: '12px 8px' }}>
                  <div style={{
                    color: '#989898',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    지급 날짜
                  </div>
                </div>
                <div style={{ width: '272px', padding: '12px 8px' }}>
                  <div style={{
                    color: '#989898',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    내용
                  </div>
                </div>
                <div style={{ width: '166px', padding: '12px 8px' }}>
                  <div style={{
                    color: '#989898',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    유형
                  </div>
                </div>
                <div style={{ width: '140px', padding: '12px 8px' }}>
                  <div style={{
                    color: '#989898',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    도장
                  </div>
                </div>
              </div>

            {/* 테이블 본문 */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: isMobile ? 'auto' : 'visible',
              display: 'flex',
              flexDirection: 'column',
              minWidth: isMobile ? '600px' : 'auto'
            }}>
              {paginatedHistory.length === 0 ? (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#989898',
                  fontSize: '14px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '400',
                  lineHeight: '21px'
                }}>
                  포인트 내역이 없습니다.
                </div>
              ) : (
                paginatedHistory.map((history, index) => (
                  <React.Fragment key={history.id}>
                    <div
                      style={{
                        borderBottom: '1px solid #F0F0F0',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '48px',
                        minWidth: isMobile ? '600px' : 'auto'
                      }}
                    >
                      <div style={{ width: '103px', padding: '12px 18px' }}>
                        <div style={{
                          color: '#161616',
                          fontSize: '14px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </div>
                      </div>
                      <div style={{ width: '166px', padding: '12px 8px' }}>
                        <div style={{
                          color: '#161616',
                          fontSize: '14px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {format(history.date, 'yyyy.MM.dd HH:mm', { locale: ko })}
                        </div>
                      </div>
                      <div style={{ width: '272px', padding: '12px 8px' }}>
                        <div style={{
                          color: '#161616',
                          fontSize: '14px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          <div>{history.reason}</div>
                          {isSalaryPayslipEntry(history) && (
                            <button
                              type="button"
                              onClick={() =>
                                setPayslipOpenId((p) => (p === history.id ? null : history.id))
                              }
                              style={{
                                marginTop: '6px',
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontFamily: 'Pretendard, sans-serif',
                                color: 'var(--Color-fill-brand-color, #FF6F0F)',
                                textDecoration: 'underline',
                              }}
                            >
                              {payslipOpenId === history.id ? '급여 명세 접기' : '급여 명세 보기'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ width: '166px', padding: '12px 8px' }}>
                        <div style={{
                          color: '#161616',
                          fontSize: '14px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {getTypeLabel(history.type)}
                        </div>
                      </div>
                      <div style={{ width: '140px', padding: '12px 8px' }}>
                        <div style={{
                          color: history.amount > 0 ? '#161616' : '#161616',
                          fontSize: '14px',
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {history.amount > 0 ? '+' : ''}{history.amount}
                        </div>
                      </div>
                    </div>
                    {isSalaryPayslipEntry(history) && payslipOpenId === history.id && (
                      <div
                        style={{
                          borderBottom: '1px solid #F0F0F0',
                          minWidth: isMobile ? '600px' : 'auto',
                        }}
                      >
                        <SalaryPayslipBreakdown
                          variant="default"
                          grossAmount={history.grossAmount}
                          taxLines={history.taxLines}
                          totalTax={history.totalTax}
                          netAmount={history.amount}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentClassPoints;
